import React, { useState, useEffect, useCallback } from 'react';
import { css } from 'twin.macro';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getTeamIdFromURL, isAdminPanel, fetchWrapper } from '../../../../_helpers';
import { AdminTable } from '../../reusableStyles';
import LoadingSpiner from '../../../../_elements/LoadingSpiner';
import Pagination from '../../Pagination';
import { useSortData } from '../../useSortData';
import ExpenseDetailsModal from './ExpenseDetailsModal';
import ExpenseRejectModal from './ExpenseRejectModal';
import { ActionStyles } from '../../reusableStyles';
import ExpenseApproveModal from './ExpenseApproveModal';
import { getStatusBadge, statusConfig } from './utils';
import { ViewUserForm } from '../../Form/SubmittedForm';
import { FaWpforms } from 'react-icons/fa';
import ExpenseEditModal from './ExpenseEditModal';
import EvvSearchModalComponent from '../EvvSearchModal';
import { getExpenses } from '@/_services/api/expense';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import dayjs from 'dayjs';

const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
    }).format(amount);
};

export const formatDateShort = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const Expenses = ({ teams, currUser }) => {
    const isUserEvv = !!useLocation().pathname.match(/user-evv/);
    const isUserPayroll = !!useLocation().pathname.match(/user-payroll/);
    const isTeamSettings = isUserEvv || isUserPayroll;
    const teamId = getTeamIdFromURL();
    const _isAdminPanel = isAdminPanel();

    // State management
    const [expenses, setExpenses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingProvider, setLoadingProvider] = useState(false);
    const [provider, setProvider] = useState({});
    const [data, setData] = useState({});
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [client, setClient] = useState(null);
    const [userEmployee, setUserEmployee] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [expenseStatusFilter, setExpenseStatusFilter] = useState(null);

    // functions
    const handleUserEmployeeSelect = (selectedUser) => {
        setUserEmployee(selectedUser);
    };
    const handleProviderSelect = (selectedProvider) => {
        setProvider(selectedProvider);
    };
    const handleClientSelect = (selectedClient) => {
        setClient(selectedClient);
    };

    const handleEndDateChange = (date) => {
        if (!date) {
            setEndDate(null);
            return;
        }
        setEndDate(date.toISOString());
    };
    const handleStartDateChange = (date) => {
        if (!date) {
            setStartDate(null);
            return;
        }
        setStartDate(date.toISOString());
    };
    const handleStartDateClear = () => {
        setStartDate(null);
    };
    const handleEndDateClear = () => {
        setEndDate(null);
    };

    const handleChangeExpenseStatus = (value) => {
        setExpenseStatusFilter(value);
    };

    const handleClearExpenseStatus = () => {
        setExpenseStatusFilter(null);
    };

    const fetchExpenses = useCallback(
        async (options = {}) => {
            try {
                setIsLoading(true);
                const response = await getExpenses({
                    provider: provider?.value,
                    employee: userEmployee?._id || userEmployee?._id,
                    client: client ? client?.id || client?.id : '',
                    startDate,
                    endDate,
                    page: currentPage,
                    team: teamId,
                    status: expenseStatusFilter,
                });
                setData(response);
                setExpenses(response.results);
            } catch (error) {
                if (error?.name === 'AbortError') return;
                toast.error(error.message || 'Failed to fetch expenses');
            } finally {
                setIsLoading(false);
            }
        },
        [teamId, provider?.value, userEmployee?.id, client, startDate, endDate, currentPage, expenseStatusFilter]
    );

    const fetchProviderData = useCallback(async () => {
        if (!isTeamSettings || !teamId) {
            return;
        }
        const team = (teams || []).find((team) => team.id === teamId);

        try {
            setLoadingProvider(true);
            const {
                results: [response],
            } = await fetchWrapper.get(`evv/provider?team=${teamId}`);

            setProvider({
                value: response?._id,
                label: response?.agencyName,
                providerId: response?._id,
                teamId: team?.id,
                agencyName: response?.agencyName,
                team: response?._id,
                state: response?.state,
                payroll: response?.payroll,
            });
        } catch (error) {
            if (error.name !== 'AbortError') {
                toast.error("Couldn't fetch provider data");
            }
        } finally {
            setLoadingProvider(false);
        }
    }, [teamId, teams, isTeamSettings]);

    const { requestSort, sortConfig } = useSortData(fetchExpenses, {
        key: 'receiptDate',
        direction: 'desc',
    });

    useEffect(() => {
        if (isTeamSettings && teamId) {
            fetchProviderData();
        }
    }, []);

    useEffect(() => {
        if (_isAdminPanel || provider?.value) {
            fetchExpenses({ sortBy: `${sortConfig.key}:${sortConfig.direction}` });
        }
    }, [fetchExpenses, sortConfig, _isAdminPanel, provider?.value]);

    const onPageChanged = ({ currentPage } = {}) => {
        setCurrentPage(Math.max(currentPage || 1, 1));
    };
    const handleClearAll = () => {
        setUserEmployee(null);
        if (!isTeamSettings) {
            setProvider(null);
        }
        handleEndDateClear();
        handleStartDateClear();
    };

    const handleUpdateExpense = (updatedData) => {
        setExpenses((prevState) =>
            prevState.map((item) => (item?._id && item._id === updatedData?._id ? { ...item, ...updatedData } : item))
        );
    };

    return (
        <div>
            <div
                className="d-flex flex-row align-items-center mb-4"
                css={css`
                    justify-content: space-between;
                `}
            >
                <EvvSearchModalComponent
                    type="Visit"
                    isTeamSettings={isTeamSettings}
                    isChannelSettings={false}
                    client={client}
                    userEmployee={userEmployee}
                    provider={provider}
                    handleUserEmployeeSelect={handleUserEmployeeSelect}
                    handleProviderSelect={handleProviderSelect}
                    handleClearAll={handleClearAll}
                    handleClientSelect={handleClientSelect}
                    endDate={endDate}
                    startDate={startDate}
                    handleEndDateChange={handleEndDateChange}
                    handleStartDateChange={handleStartDateChange}
                    handleStartDateClear={handleStartDateClear}
                    handleEndDateClear={handleEndDateClear}
                    team={teamId}
                    showIsManualFilter={false}
                    modalTitle={'Expense'}
                    labels={Object.fromEntries(Object.entries(statusConfig).map(([key, value]) => [key, value.color]))}
                    visitLabelFilter={expenseStatusFilter}
                    handleClearVisitLabelFilter={handleClearExpenseStatus}
                    handleVisitLabelFilterChange={handleChangeExpenseStatus}
                />
            </div>
            <div
                className="d-flex flex-row align-items-center mb-4"
                css={css`
                    justify-content: space-between;
                `}
            >
                <div className="d-flex align-items-center"></div>
            </div>

            <div className="custom-card card-table d-flex flex-column justify-content-between">
                <AdminTable className="w-100 text-dark-grey">
                    <thead>
                        <tr>
                            <th className="pointer">SN</th>
                            {_isAdminPanel && <th>Agency Name</th>}
                            <th>Client</th>
                            <th>Employee</th>
                            <th>Amount</th>
                            {/* <th>Vendor/Merchant</th> */}
                            {/* <th className="pointer">Category</th> */}
                            <th
                            // onClick={() => requestSort('receiptDate')}
                            // className="pointer costume-tooltip"
                            // data-tooltip="sort by receipt date"
                            >
                                Receipt Date
                                {/* <SortedTableHeaderIcon sortConfig={sortConfig} fieldName="receiptDate" /> */}
                            </th>
                            <th
                            // onClick={() => requestSort('createdAt')}
                            // className="pointer costume-tooltip"
                            // data-tooltip="sort by created at"
                            >
                                Submitted At
                                {/* <SortedTableHeaderIcon sortConfig={sortConfig} fieldName="createdAt" /> */}
                            </th>
                            <th className="pointer">Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-light-grey">
                        {isLoading || loadingProvider ? (
                            <tr>
                                <td colSpan={8} rowSpan={10}>
                                    <LoadingSpiner size={80} />
                                </td>
                            </tr>
                        ) : expenses.length > 0 ? (
                            expenses.map((expense, index) => (
                                <tr key={expense._id}>
                                    <td>{index + 1}</td>
                                    {_isAdminPanel && <td>{(expense.providerData || {})?.agencyName}</td>}
                                    <td>
                                        {(expense?.clientData || {})?.firstName} {(expense?.clientData || {})?.lastName}
                                    </td>
                                    <td>{((expense.employeeData || {})?.user || {})?.name || ''}</td>
                                    <td>{formatCurrency(expense.amount, expense.currency)}</td>
                                    {/* <td>
                                        <span
                                            title={expense.vendor}
                                            style={{
                                                display: 'inline-block',
                                                maxWidth: '150px',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {expense.vendor}
                                        </span>
                                    </td> */}
                                    <td>{dayjs(expense.receiptDate).format('MM-DD-YYYY')}</td>
                                    <td>{dayjs(expense.createdAt).format('MM-DD-YYYY, h:mm A')}</td>
                                    <td>{getStatusBadge(expense.status)}</td>
                                    <td>
                                        <ActionStyles>
                                            <ExpenseDetailsModal
                                                expenseObj={expense}
                                                handleUpdateExpense={handleUpdateExpense}
                                            />
                                            {expense?.userFormData && (
                                                <ViewUserForm id={expense?.userFormData}>
                                                    <BootstrapToolTip text="Form">
                                                        <FaWpforms className="icon " />
                                                    </BootstrapToolTip>
                                                </ViewUserForm>
                                            )}
                                            {expense.status === 'pending_approval' && (
                                                <>
                                                    <ExpenseEditModal
                                                        expense={expense}
                                                        handleUpdateExpense={handleUpdateExpense}
                                                    />
                                                    <ExpenseApproveModal
                                                        expense={expense}
                                                        handleUpdateExpense={handleUpdateExpense}
                                                    />
                                                    <ExpenseRejectModal
                                                        expense={expense}
                                                        handleUpdateExpense={handleUpdateExpense}
                                                    />
                                                </>
                                            )}
                                        </ActionStyles>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={8} className="text-center">
                                    No expenses found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </AdminTable>

                {data.totalResults ? (
                    <div className="m-4">
                        <Pagination
                            pageNo={currentPage}
                            totalRecords={data.totalResults}
                            pageLimit={10}
                            pageNeighbours={1}
                            onPageChanged={onPageChanged}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    teams: state.teams.teams,
    currUser: state.user.user,
});

export default connect(mapStateToProps)(Expenses);
